/**
 * Apotheca Wellness Circles
 * ─────────────────────────
 * Community-driven circle pricing for Apotheca products.
 *
 * Two modes:
 *   1. Product page panel (PDP) — shows circle tiers, "Create your circle" CTA
 *   2. Join page (/pages/group-buy?code=abc123) — live circle status, join CTA
 *
 * Calls Supabase edge function directly (not through Shopify proxy) since
 * group data is largely public. Customer identity comes from window.__apothecaCustomer.
 */

(function() {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────
  const SUPABASE_URL = 'https://dvvcdxxtfyonrqlijfyb.supabase.co';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR2dmNkeHh0ZnlvbnJxbGlqZnliIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4MzkzNjYsImV4cCI6MjA4ODQxNTM2Nn0.0IwJzOPvoodlzQSwG3yLp7yZDR-5AQC5jslvNlIdnzs';
  const FUNCTION_URL = SUPABASE_URL + '/functions/v1/group-buy';
  const STORE_URL = 'https://apotheca.market';

  // Tier configs (match edge function defaults)
  const PEPTIDE_TIERS = [
    { min_members: 3, discount_pct: 15, label: 'Circle of 3' },
    { min_members: 5, discount_pct: 20, label: 'Circle of 5' },
    { min_members: 10, discount_pct: 25, label: 'Circle of 10+' },
  ];
  const SUPPLEMENT_TIERS = [
    { min_members: 3, discount_pct: 10, label: 'Circle of 3' },
    { min_members: 5, discount_pct: 15, label: 'Circle of 5' },
    { min_members: 10, discount_pct: 18, label: 'Circle of 10+' },
  ];

  // ─── API calls ─────────────────────────────────────────────────────
  async function apiCall(action, params, body) {
    var queryParts = ['action=' + encodeURIComponent(action)];
    if (params) {
      Object.keys(params).forEach(function(k) {
        queryParts.push(encodeURIComponent(k) + '=' + encodeURIComponent(params[k]));
      });
    }
    var url = FUNCTION_URL + '?' + queryParts.join('&');
    var opts = {
      method: body ? 'POST' : 'GET',
      headers: {
        'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
        'apikey': SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
    };
    if (body) opts.body = JSON.stringify(body);
    var resp = await fetch(url, opts);
    return resp.json();
  }

  // ─── Customer helper ───────────────────────────────────────────────
  function getCustomer() {
    if (window.__apothecaCustomer && window.__apothecaCustomer.id) {
      return window.__apothecaCustomer;
    }
    return null;
  }

  // ─── Timer formatting ─────────────────────────────────────────────
  function formatTimeLeft(expiresAt) {
    var now = Date.now();
    var end = new Date(expiresAt).getTime();
    var diff = end - now;
    if (diff <= 0) return 'Expired';
    var hours = Math.floor(diff / 3600000);
    var minutes = Math.floor((diff % 3600000) / 60000);
    var seconds = Math.floor((diff % 60000) / 1000);
    if (hours > 0) return hours + 'h ' + minutes + 'm left';
    if (minutes > 0) return minutes + 'm ' + seconds + 's left';
    return seconds + 's left';
  }

  // ─── Share URL ─────────────────────────────────────────────────────
  function getShareUrl(code) {
    return STORE_URL + '/pages/group-buy?code=' + code;
  }

  // ─── Native share or copy fallback ─────────────────────────────────
  async function shareGroup(code, productTitle, discountPct) {
    var url = getShareUrl(code);
    var text = "I started a Wellness Circle for " + productTitle + " on Apotheca. Join my circle and we all save " + discountPct + "%. " + url;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my Apotheca Wellness Circle',
          text: text,
          url: url,
        });
        return true;
      } catch(e) {
        // User cancelled or not supported, fall through to copy
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch(e) {
      // Final fallback
      var textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return 'copied';
    }
  }

  // ─── PDP Panel: render tier pricing ────────────────────────────────
  function renderTierBar(tiers, priceCents) {
    var html = '<div class="gb-tiers">';
    html += '<div class="gb-tier gb-tier-solo gb-tier-active">';
    html += '<div class="gb-tier-label">Solo</div>';
    html += '<div class="gb-tier-price">' + formatPrice(priceCents) + '</div>';
    html += '</div>';

    tiers.forEach(function(tier) {
      var discountedCents = Math.round(priceCents * (1 - tier.discount_pct / 100));
      html += '<div class="gb-tier">';
      html += '<div class="gb-tier-label">' + tier.label + ' (' + tier.min_members + ')</div>';
      html += '<div class="gb-tier-price">' + formatPrice(discountedCents) + '</div>';
      html += '<div class="gb-tier-save">Save ' + tier.discount_pct + '%</div>';
      html += '</div>';
    });

    html += '</div>';
    return html;
  }

  function formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ─── PDP Panel: full render ────────────────────────────────────────
  function renderPDPPanel(container, product) {
    var isPeptide = product.type === 'peptide';
    var tiers = isPeptide ? PEPTIDE_TIERS : SUPPLEMENT_TIERS;
    var priceCents = product.price_cents;
    var maxDiscount = tiers[tiers.length - 1].discount_pct;

    var html = '';
    html += '<div class="gb-panel">';
    html += '<div class="gb-header">';
    html += '<div class="gb-header-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>';
    html += '<div class="gb-header-text">';
    html += '<div class="gb-header-title">Start a Wellness Circle</div>';
    html += '<div class="gb-header-sub">Invite friends and everyone unlocks the same lower price, up to ' + maxDiscount + '% off.</div>';
    html += '</div>';
    html += '</div>';

    html += renderTierBar(tiers, priceCents);

    html += '<button class="gb-start-btn" id="gbStartBtn">';
    html += '<span>Create your circle</span>';
    html += '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
    html += '</button>';

    // Active groups (social proof) — filled async
    html += '<div class="gb-social-proof" id="gbSocialProof" style="display:none;"></div>';

    html += '<div class="gb-compliance">No commissions or referral fees. Just shared pricing for people ordering together.</div>';
    html += '</div>';

    container.innerHTML = html;

    // Wire up start button
    document.getElementById('gbStartBtn').addEventListener('click', function() {
      handleStartGroup(product, tiers, container);
    });

    // Load active groups
    loadActiveGroups(product.id);

    // Check if this customer already has a circle for this product.
    // If so, replace the "Create" panel with the share state.
    checkExistingCircle(container, product);
  }

  // ─── Create your circle ─────────────────────────────────────────────────
  async function handleStartGroup(product, tiers, container) {
    var customer = getCustomer();
    var btn = document.getElementById('gbStartBtn');
    btn.disabled = true;
    btn.innerHTML = '<span>Creating group...</span>';

    var body = {
      product_id: product.id,
      variant_id: product.variant_id,
      product_title: product.title,
      product_handle: product.handle,
      product_image_url: product.image_url,
      product_price_cents: product.price_cents,
      product_type: product.type,
      initiator_name: customer ? (customer.first_name || 'Someone') : 'Someone',
      initiator_email: customer ? customer.email : null,
    };

    try {
      var result = await apiCall('create', {}, body);
      if (result.error) {
        btn.disabled = false;
        btn.innerHTML = '<span>Create your circle</span>';
        alert('Could not create circle: ' + result.error);
        return;
      }

      // Show the share state
      renderShareState(container, result.group, product);
    } catch(e) {
      btn.disabled = false;
      btn.innerHTML = '<span>Create your circle</span>';
      console.error('[GroupBuy] create error:', e);
    }
  }

  // ─── Share state (after creating a group) ──────────────────────────
  function renderShareState(container, group, product) {
    var tiers = group.discount_tiers || SUPPLEMENT_TIERS;
    var priceCents = group.product_price_cents || product.price_cents;

    var html = '<div class="gb-panel gb-panel-active">';
    html += '<div class="gb-created-header">';
    html += '<div class="gb-check-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#4A5440" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg></div>';
    html += '<div class="gb-created-title">Circle created!</div>';
    html += '</div>';
    html += '<div class="gb-created-sub">Share your link. The more people who join, the more everyone saves.</div>';

    // Progressive tier roadmap — shows all tiers with "unlock" language
    html += '<div class="gb-tier-roadmap">';
    tiers.forEach(function(tier, i) {
      var discountedCents = Math.round(priceCents * (1 - tier.discount_pct / 100));
      html += '<div class="gb-roadmap-step">';
      html += '<div class="gb-roadmap-dot"></div>';
      html += '<div class="gb-roadmap-info">';
      html += '<span class="gb-roadmap-target">' + tier.min_members + ' people</span>';
      html += '<span class="gb-roadmap-reward">' + tier.discount_pct + '% off · ' + formatPrice(discountedCents) + ' each</span>';
      html += '</div>';
      html += '</div>';
    });
    html += '</div>';

    // Timer
    html += '<div class="gb-timer" id="gbTimer" data-expires="' + group.expires_at + '">' + formatTimeLeft(group.expires_at) + '</div>';

    // Share URL display
    var shareUrl = getShareUrl(group.share_code);
    html += '<div class="gb-share-url">';
    html += '<input type="text" class="gb-share-input" value="' + shareUrl + '" readonly id="gbShareInput">';
    html += '<button class="gb-copy-btn" id="gbCopyBtn">Copy</button>';
    html += '</div>';

    // Share buttons
    html += '<div class="gb-share-btns">';
    html += '<button class="gb-share-btn gb-share-native" id="gbShareNative">';
    html += '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';
    html += ' Invite to your circle';
    html += '</button>';
    html += '</div>';

    html += '<div class="gb-member-count">1 person in this circle · invite ' + (tiers[0].min_members - 1) + ' more to unlock ' + tiers[0].discount_pct + '% off</div>';

    html += '<div class="gb-compliance">Everyone in your circle gets the same price. No commissions, no referral fees — just better pricing together.</div>';
    html += '</div>';

    container.innerHTML = html;

    // Wire events
    document.getElementById('gbCopyBtn').addEventListener('click', function() {
      var input = document.getElementById('gbShareInput');
      input.select();
      navigator.clipboard.writeText(input.value).then(function() {
        document.getElementById('gbCopyBtn').textContent = 'Copied!';
        setTimeout(function() { document.getElementById('gbCopyBtn').textContent = 'Copy'; }, 2000);
      });
    });

    document.getElementById('gbShareNative').addEventListener('click', function() {
      shareGroup(group.share_code, product.title, firstTier.discount_pct);
    });

    // Start timer
    startTimer();

    // Listen for realtime updates (member joins)
    listenForGroupUpdates(group.share_code, container, product);
  }

  // ─── Timer tick ────────────────────────────────────────────────────
  function startTimer() {
    var timerEl = document.getElementById('gbTimer');
    if (!timerEl) return;
    var interval = setInterval(function() {
      timerEl = document.getElementById('gbTimer');
      if (!timerEl) { clearInterval(interval); return; }
      var expires = timerEl.getAttribute('data-expires');
      var text = formatTimeLeft(expires);
      timerEl.textContent = text;
      if (text === 'Expired') {
        clearInterval(interval);
        timerEl.classList.add('gb-timer-expired');
      }
    }, 1000);
  }

  // ─── Realtime: listen for member joins via Supabase ────────────────
  function listenForGroupUpdates(shareCode, container, product) {
    // Use Supabase Realtime via the JS client
    // For v1, we'll poll every 10 seconds. Realtime subscription can replace this.
    var pollInterval = setInterval(async function() {
      try {
        var result = await apiCall('status', { code: shareCode });
        if (!result || !result.group) return;

        var countEl = container.querySelector('.gb-member-count');
        if (countEl && result.group.member_count > 1) {
          var tiers = result.group.discount_tiers || SUPPLEMENT_TIERS;
          var currentTier = result.group.current_tier;

          if (currentTier && currentTier.discount_pct > 0 && result.group.discount_code) {
            // Tier unlocked — show discount code + hint about next tier
            var msg = '<strong>' + currentTier.label + ' pricing unlocked! ' + currentTier.discount_pct + '% off</strong>';
            msg += '<br>Code: <code>' + result.group.discount_code + '</code>';
            if (currentTier.next_tier) {
              msg += '<br><span style="font-size:11px;color:#8A8178;">Invite ' + (currentTier.next_tier.min_members - result.group.member_count) + ' more for ' + currentTier.next_tier.discount_pct + '% off</span>';
            }
            countEl.innerHTML = msg;
          } else {
            // Not yet at first tier
            var needed = tiers[0].min_members - result.group.member_count;
            countEl.textContent = result.group.member_count + ' people in this circle · invite ' + needed + ' more to unlock ' + tiers[0].discount_pct + '% off';
          }
        }

        // Update roadmap dots to show progress
        var dots = container.querySelectorAll('.gb-roadmap-step');
        if (dots.length) {
          tiers.forEach(function(tier, i) {
            if (dots[i] && result.group.member_count >= tier.min_members) {
              dots[i].classList.add('gb-roadmap-step-reached');
            }
          });
        }

        if (result.group.status === 'expired') {
          clearInterval(pollInterval);
        }
      } catch(e) { /* no-op */ }
    }, 10000);
  }

  // ─── Active groups social proof ────────────────────────────────────
  async function loadActiveGroups(productId) {
    try {
      var result = await apiCall('active', { product_id: productId });
      if (!result) return;

      var proofEl = document.getElementById('gbSocialProof');
      if (!proofEl) return;

      var parts = [];
      if (result.groups_filled_today > 0) {
        parts.push(result.groups_filled_today + ' circle' + (result.groups_filled_today > 1 ? 's' : '') + ' completed today');
      }
      if (result.active_groups && result.active_groups.length > 0) {
        var openCount = result.active_groups.filter(function(g) { return g.status === 'open'; }).length;
        if (openCount > 0) {
          parts.push(openCount + ' active circle' + (openCount > 1 ? 's' : '') + ' now');
        }
      }

      if (parts.length > 0) {
        proofEl.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> ' + parts.join(' · ');
        proofEl.style.display = 'flex';
      }
    } catch(e) { /* no-op */ }
  }

  // ─── JOIN PAGE: render full group status ────────────────────────────
  async function renderJoinPage(container, shareCode) {
    container.innerHTML = '<div class="gb-loading">Loading group...</div>';

    try {
      var result = await apiCall('status', { code: shareCode });
      if (!result || !result.group) {
        container.innerHTML = '<div class="gb-error">This Wellness Circle was not found or has closed.</div>';
        return;
      }

      var group = result.group;
      var members = result.members || [];
      var isMember = result.is_member;
      var tiers = group.discount_tiers || SUPPLEMENT_TIERS;
      var currentTier = group.current_tier;
      var priceCents = group.product_price_cents;

      var html = '<div class="gb-join-page">';

      // Product header
      html += '<div class="gb-join-product">';
      if (group.product_image_url) {
        html += '<img src="' + group.product_image_url + '" alt="' + group.product_title + '" class="gb-join-img">';
      }
      html += '<div class="gb-join-product-info">';
      html += '<div class="gb-join-product-title">' + group.product_title + '</div>';
      html += '<div class="gb-join-product-price">' + formatPrice(priceCents) + ' full price</div>';
      html += '</div>';
      html += '</div>';

      // Who started it
      html += '<div class="gb-join-initiator">';
      html += (group.initiator_name || 'Someone') + ' started this circle';
      html += '</div>';

      // Timer
      if (group.status === 'open' || group.status === 'filled') {
        html += '<div class="gb-timer" id="gbTimer" data-expires="' + group.expires_at + '">' + formatTimeLeft(group.expires_at) + '</div>';
      } else if (group.status === 'expired') {
        html += '<div class="gb-timer gb-timer-expired">This circle has closed</div>';
      }

      // Tier progress
      html += '<div class="gb-join-progress">';
      html += '<div class="gb-join-count">' + group.member_count + ' people in this circle</div>';
      tiers.forEach(function(tier, i) {
        var reached = group.member_count >= tier.min_members;
        var discountedCents = Math.round(priceCents * (1 - tier.discount_pct / 100));
        html += '<div class="gb-join-tier ' + (reached ? 'gb-join-tier-reached' : '') + '">';
        html += '<div class="gb-join-tier-dot"></div>';
        html += '<div class="gb-join-tier-info">';
        html += '<span class="gb-join-tier-label">' + tier.label + ' (' + tier.min_members + ')</span>';
        html += '<span class="gb-join-tier-price">' + formatPrice(discountedCents) + ' each · ' + tier.discount_pct + '% off</span>';
        html += '</div>';
        if (reached) html += '<span class="gb-join-tier-check">✓</span>';
        html += '</div>';
      });
      html += '</div>';

      // Members list
      html += '<div class="gb-join-members">';
      members.forEach(function(m) {
        html += '<div class="gb-join-member">';
        html += '<div class="gb-join-member-avatar">' + ((m.member_name || 'S')[0]).toUpperCase() + '</div>';
        html += '<span>' + (m.member_name || 'Someone') + (m.is_initiator ? ' (started)' : '') + '</span>';
        html += '</div>';
      });
      html += '</div>';

      // Discount code (if tier reached)
      if (group.discount_code && (group.status === 'filled' || currentTier.discount_pct > 0)) {
        html += '<div class="gb-join-discount">';
        html += '<div class="gb-join-discount-label">' + currentTier.label + ' pricing unlocked! ' + currentTier.discount_pct + '% off</div>';
        html += '<div class="gb-join-discount-code">' + group.discount_code + '</div>';
        html += '<a href="/products/' + group.product_handle + '?discount=' + group.discount_code + '" class="gb-join-shop-btn">Shop now with ' + currentTier.discount_pct + '% off</a>';
        html += '</div>';
      }

      // Join CTA (if not already a member and group is still open/filled)
      if (!isMember && (group.status === 'open' || group.status === 'filled')) {
        html += '<button class="gb-join-btn" id="gbJoinBtn">';
        var nextDiscount = currentTier.discount_pct > 0 ? currentTier.discount_pct : tiers[0].discount_pct;
        html += 'Join this circle · save ' + nextDiscount + '%';
        html += '</button>';
      } else if (isMember) {
        html += '<div class="gb-join-already">You\'re in this circle! Invite more people to unlock better pricing.</div>';

        // Share buttons for existing members
        html += '<div class="gb-share-btns">';
        html += '<button class="gb-share-btn gb-share-native" id="gbShareNative">';
        html += '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';
        html += ' Invite more people';
        html += '</button>';
        html += '</div>';
      }

      html += '<div class="gb-compliance">Everyone in your circle gets the same price. No commissions, no referral fees — just better pricing together.</div>';
      html += '</div>';

      container.innerHTML = html;

      // Wire join button
      var joinBtn = document.getElementById('gbJoinBtn');
      if (joinBtn) {
        joinBtn.addEventListener('click', function() {
          handleJoinGroup(shareCode, group, container);
        });
      }

      // Wire share button
      var shareBtn = document.getElementById('gbShareNative');
      if (shareBtn) {
        shareBtn.addEventListener('click', function() {
          var disc = currentTier.discount_pct > 0 ? currentTier.discount_pct : tiers[0].discount_pct;
          shareGroup(shareCode, group.product_title, disc);
        });
      }

      // Start timer
      startTimer();

    } catch(e) {
      container.innerHTML = '<div class="gb-error">Could not load this circle. Please try again.</div>';
      console.error('[GroupBuy] join page error:', e);
    }
  }

  // ─── Join an existing group ────────────────────────────────────────
  async function handleJoinGroup(shareCode, group, container) {
    var customer = getCustomer();
    var btn = document.getElementById('gbJoinBtn');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Joining...';
    }

    var body = {
      member_name: customer ? (customer.first_name || 'Someone') : 'Someone',
      member_email: customer ? customer.email : null,
    };

    try {
      var result = await apiCall('join', { code: shareCode }, body);
      if (result.error) {
        if (result.already_member) {
          // Reload the page to show "you're in" state
          renderJoinPage(container, shareCode);
          return;
        }
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Join this circle';
        }
        alert(result.error);
        return;
      }

      // Reload the page to show updated state
      renderJoinPage(container, shareCode);

    } catch(e) {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Join this circle';
      }
      console.error('[GroupBuy] join error:', e);
    }
  }

  // ─── Fetch my circles ───────────────────────────────────────────────
  async function fetchMyCircles() {
    var customer = getCustomer();
    if (!customer) return [];
    try {
      var result = await apiCall('my-circles', {});
      return (result && result.circles) ? result.circles : [];
    } catch(e) { return []; }
  }

  // ─── 1. PDP: show existing circle instead of "Create" ─────────────
  // Called by the PDP panel init. If the customer already has an active
  // circle for this product, show the share state instead of the create CTA.
  async function checkExistingCircle(container, product) {
    var customer = getCustomer();
    if (!customer) return false;

    var circles = await fetchMyCircles();
    var activeCircle = null;
    for (var i = 0; i < circles.length; i++) {
      if (circles[i].shopify_product_id == product.id &&
          (circles[i].status === 'open' || circles[i].status === 'filled')) {
        activeCircle = circles[i];
        break;
      }
    }
    if (!activeCircle) return false;

    // Render the share state for the existing circle
    renderShareState(container, activeCircle, product);
    return true;
  }

  // ─── 2. Cart page: circle reminder above checkout ──────────────────
  async function renderCartReminder(container) {
    var customer = getCustomer();
    if (!customer) return;

    var circles = await fetchMyCircles();
    var activeCircles = circles.filter(function(c) {
      return c.status === 'open' || c.status === 'filled';
    });

    if (activeCircles.length === 0) return;

    var html = '<div class="gb-cart-reminders">';
    activeCircles.forEach(function(circle) {
      var tiers = circle.discount_tiers || SUPPLEMENT_TIERS;
      var tierInfo = circle.current_tier;
      var shareUrl = circle.share_url || getShareUrl(circle.share_code);

      html += '<div class="gb-cart-reminder">';
      html += '<div class="gb-cart-reminder-left">';
      html += '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#4A5440" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>';
      html += '<div class="gb-cart-reminder-info">';
      html += '<div class="gb-cart-reminder-title">Wellness Circle: ' + circle.product_title + '</div>';

      if (tierInfo && tierInfo.discount_pct > 0 && circle.discount_code) {
        html += '<div class="gb-cart-reminder-status">' + circle.member_count + ' people · <strong>' + tierInfo.discount_pct + '% off unlocked</strong> · Code: <code>' + circle.discount_code + '</code></div>';
        if (tierInfo.next_tier) {
          html += '<div class="gb-cart-reminder-hint">Invite ' + (tierInfo.next_tier.min_members - circle.member_count) + ' more for ' + tierInfo.next_tier.discount_pct + '% off</div>';
        }
      } else {
        var needed = tiers[0].min_members - circle.member_count;
        html += '<div class="gb-cart-reminder-status">' + circle.member_count + ' of ' + tiers[0].min_members + ' people · invite ' + needed + ' more for ' + tiers[0].discount_pct + '% off</div>';
      }
      html += '</div>'; // info
      html += '</div>'; // left

      html += '<div class="gb-cart-reminder-actions">';
      html += '<button class="gb-cart-share-btn" data-code="' + circle.share_code + '" data-title="' + circle.product_title + '" data-pct="' + (tierInfo.discount_pct || tiers[0].discount_pct) + '">';
      html += '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>';
      html += ' Invite';
      html += '</button>';
      html += '<button class="gb-cart-copy-btn" data-url="' + shareUrl + '">Copy link</button>';
      html += '</div>';

      // Timer
      if (circle.status === 'open') {
        html += '<div class="gb-cart-reminder-timer" data-expires="' + circle.expires_at + '">Circle closes in ' + formatTimeLeft(circle.expires_at) + '</div>';
      }

      html += '</div>'; // reminder
    });
    html += '</div>';

    container.innerHTML = html;

    // Wire share/copy buttons
    container.querySelectorAll('.gb-cart-share-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        shareGroup(btn.dataset.code, btn.dataset.title, parseInt(btn.dataset.pct));
      });
    });
    container.querySelectorAll('.gb-cart-copy-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        navigator.clipboard.writeText(btn.dataset.url).then(function() {
          btn.textContent = 'Copied!';
          setTimeout(function() { btn.textContent = 'Copy link'; }, 2000);
        });
      });
    });

    // Timer tick
    setInterval(function() {
      container.querySelectorAll('.gb-cart-reminder-timer').forEach(function(el) {
        var expires = el.dataset.expires;
        el.textContent = 'Circle closes in ' + formatTimeLeft(expires);
      });
    }, 1000);
  }

  // ─── 3. My Circles account page ───────────────────────────────────
  async function renderMyCircles(container) {
    var customer = getCustomer();
    if (!customer) {
      container.innerHTML = '<div class="gb-my-login">Log in to see your Wellness Circles.</div>';
      return;
    }

    container.innerHTML = '<div class="gb-loading">Loading your circles...</div>';

    var circles = await fetchMyCircles();
    if (circles.length === 0) {
      container.innerHTML = '<div class="gb-my-empty">';
      container.innerHTML += '<div class="gb-my-empty-title">No Wellness Circles yet</div>';
      container.innerHTML += '<div class="gb-my-empty-sub">Start a circle from any product page to save with friends.</div>';
      container.innerHTML += '<a href="/collections/shop-all" class="gb-my-browse-btn">Browse products</a>';
      container.innerHTML += '</div>';
      return;
    }

    var active = circles.filter(function(c) { return c.status === 'open' || c.status === 'filled'; });
    var past = circles.filter(function(c) { return c.status !== 'open' && c.status !== 'filled'; });

    var html = '';

    if (active.length > 0) {
      html += '<div class="gb-my-section">';
      html += '<div class="gb-my-section-title">Active Circles</div>';
      active.forEach(function(circle) {
        html += renderCircleCard(circle, true);
      });
      html += '</div>';
    }

    if (past.length > 0) {
      html += '<div class="gb-my-section">';
      html += '<div class="gb-my-section-title">Past Circles</div>';
      past.forEach(function(circle) {
        html += renderCircleCard(circle, false);
      });
      html += '</div>';
    }

    container.innerHTML = html;

    // Wire share/copy buttons
    container.querySelectorAll('.gb-card-share-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        shareGroup(btn.dataset.code, btn.dataset.title, parseInt(btn.dataset.pct));
      });
    });
    container.querySelectorAll('.gb-card-copy-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        navigator.clipboard.writeText(btn.dataset.url).then(function() {
          btn.textContent = 'Copied!';
          setTimeout(function() { btn.textContent = 'Copy link'; }, 2000);
        });
      });
    });
  }

  function renderCircleCard(circle, isActive) {
    var tiers = circle.discount_tiers || SUPPLEMENT_TIERS;
    var tierInfo = circle.current_tier;
    var shareUrl = circle.share_url || getShareUrl(circle.share_code);
    var statusLabel = circle.status === 'open' ? 'Active' : circle.status === 'filled' ? 'Filled' : circle.status === 'expired' ? 'Closed' : circle.status === 'completed' ? 'Completed' : circle.status;

    var html = '<div class="gb-circle-card ' + (isActive ? '' : 'gb-circle-card-past') + '">';

    // Product row
    html += '<div class="gb-card-product">';
    if (circle.product_image_url) {
      html += '<img src="' + circle.product_image_url + '" class="gb-card-img" alt="">';
    }
    html += '<div class="gb-card-product-info">';
    html += '<div class="gb-card-product-title">' + circle.product_title + '</div>';
    html += '<div class="gb-card-meta">' + circle.member_count + ' people · ' + statusLabel;
    if (isActive) {
      html += ' · ' + formatTimeLeft(circle.expires_at);
    }
    html += '</div>';
    html += '</div>'; // product-info

    // Status badge
    html += '<div class="gb-card-badge gb-card-badge-' + circle.status + '">' + statusLabel + '</div>';
    html += '</div>'; // product row

    // Discount code if unlocked
    if (tierInfo && tierInfo.discount_pct > 0 && circle.discount_code) {
      html += '<div class="gb-card-discount">';
      html += '<span>' + tierInfo.label + ' pricing · ' + tierInfo.discount_pct + '% off</span>';
      html += '<code>' + circle.discount_code + '</code>';
      html += '</div>';
    }

    // Actions for active circles
    if (isActive) {
      html += '<div class="gb-card-actions">';
      html += '<button class="gb-card-share-btn" data-code="' + circle.share_code + '" data-title="' + circle.product_title + '" data-pct="' + (tierInfo.discount_pct || tiers[0].discount_pct) + '">';
      html += 'Invite people';
      html += '</button>';
      html += '<button class="gb-card-copy-btn" data-url="' + shareUrl + '">Copy link</button>';
      html += '<a href="/products/' + circle.product_handle + '" class="gb-card-view-btn">View product</a>';
      html += '</div>';

      // Tier hint
      if (tierInfo.next_tier) {
        html += '<div class="gb-card-hint">Invite ' + (tierInfo.next_tier.min_members - circle.member_count) + ' more for ' + tierInfo.next_tier.discount_pct + '% off</div>';
      } else if (tierInfo.discount_pct === 0) {
        html += '<div class="gb-card-hint">Invite ' + (tiers[0].min_members - circle.member_count) + ' more to unlock ' + tiers[0].discount_pct + '% off</div>';
      }
    }

    html += '</div>'; // card
    return html;
  }

  // ─── Expose ────────────────────────────────────────────────────────
  window.ApothecaGroupBuy = {
    renderPDPPanel: renderPDPPanel,
    renderJoinPage: renderJoinPage,
    renderCartReminder: renderCartReminder,
    renderMyCircles: renderMyCircles,
    checkExistingCircle: checkExistingCircle,
    fetchMyCircles: fetchMyCircles,
    shareGroup: shareGroup,
    PEPTIDE_TIERS: PEPTIDE_TIERS,
    SUPPLEMENT_TIERS: SUPPLEMENT_TIERS,
  };
})();
